/**
 * Build-time data, in the shape a section already knows how to render (`WEB-039`).
 *
 * Canon decision `D-015` moved four of the homepage's five reads from the browser to the
 * build. The sections that render them did not change what they show, only where it came
 * from, so this hands them the same `Resource` a live read produces — already `ready`.
 *
 * **What happens to the other three states.** `docs/TESTING.md` requires loading, empty,
 * error and data to be separately failable. For a build-time section three of them move
 * rather than disappear: *loading* and *error* now happen during the build, where a failure
 * stops the deploy instead of reaching a reader (`scripts/site-data/reads.mjs` refuses to
 * ship an empty board), and *empty* is unchanged — an empty board is still an empty board and
 * still renders as one. `reload` is therefore a no-op: there is nothing to retry, because
 * nothing failed in the browser.
 *
 * `receivedAtMs` is the moment the page loaded rather than the moment the data was read. It
 * exists for `useCountdown`, which measures against a response's `serverTime`, and no
 * build-time section runs a countdown — only `TomorrowAuction` does, and that read is still
 * live. Pinning it to the build instant instead would make any future countdown drift by the
 * age of the build, which is the one wrong answer available here.
 */
import { useMemo, useState } from "react";
import type { Resource } from "./useApiResource";

export function useStaticResource<T>(data: T): Resource<T> {
  // Sampled once, in a lazy initialiser rather than a ref: the value must be stable across
  // renders so a consumer's effect does not re-run on every paint, and `Date.now()` is impure
  // so it cannot be called in the render body.
  const [receivedAtMs] = useState(() => Date.now());

  return useMemo(
    () => ({
      state: { status: "ready", data, receivedAtMs },
      reload: () => undefined,
    }),
    [data, receivedAtMs],
  );
}
